import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Editor } from 'primereact/editor';
import { CloseIcon } from '@chakra-ui/icons';
import { TbWorldWww } from 'react-icons/tb';
import { BsFillTelephoneFill } from 'react-icons/bs';
import { FaFacebook, FaInstagram, FaWhatsapp } from 'react-icons/fa';
import { useService } from 'contexts/ServiceContext';
import { useLocation } from 'contexts/LocationContext';
import { isValidUrl } from 'helpers/helper';
import CInput from 'components/CInput';
import CButton from 'components/CButton';
import ListPDF from 'components/ListPDF';
import Dropdown from 'components/Dropdown';
import ImageUpload from 'components/ImageUpload';
import DaysTimeTable from 'components/DaysTimeTable';
import CMultipleFileUpload from 'components/CMultipleFileUpload';
import ServiceCategoryDropdown from 'components/ServiceCategoryDropdown';

export default function NewService() {
  const { createService, getSingleService, updateService } = useService();
  const { fetchAllLocations } = useLocation();

  const navigate = useNavigate();
  const params = useParams();

  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [validated, setValidated] = useState(false);
  const [isImageValid, setIsImageValid] = useState(true);
  const [locationOptions, setLocationOptions] = useState([]);
  const [newPDF, setNewPDF] = useState([]);
  const [existingPDFs, setExistingPDFs] = useState([])
  const [serviceDetail, setServiceDetail] = useState({
    name: '',
    description: '',
    address: '',
    email: '',
    location_id: '',
    logo: null,
    pdf: null,
    code: "",
    is_active: 1,
    categories: [],
    instagram: '',
    facebook: '',
    telephone: '',
    whatsapp: '',
    website: '',
    weekly_schedule: {
      monday: { start_time: null, end_time: null },
      tuesday: { start_time: null, end_time: null },
      wednesday: { start_time: null, end_time: null },
      thursday: { start_time: null, end_time: null },
      friday: { start_time: null, end_time: null },
      saturday: { start_time: null, end_time: null },
      sunday: { start_time: null, end_time: null },
    },
  });

  useEffect(() => {
    fetchSingleService();

    return () => {
      setExistingPDFs(null)
      setServiceDetail({
        name: '',
        description: '',
        address: '',
        email: '',
        location_id: '',
        logo: null,
        pdf: null,
        code: "",
        is_active: 1,
        categories: [],
        instagram: '',
        facebook: '',
        telephone: '',
        whatsapp: '',
        website: '',
        weekly_schedule: {
          monday: { start_time: null, end_time: null },
          tuesday: { start_time: null, end_time: null },
          wednesday: { start_time: null, end_time: null },
          thursday: { start_time: null, end_time: null },
          friday: { start_time: null, end_time: null },
          saturday: { start_time: null, end_time: null },
          sunday: { start_time: null, end_time: null },
        },
      });
    };
  }, [params.id]);

  const fetchSingleService = async () => {
    if (params.id) {
      const service = await getSingleService(params.id);
      if (service) {
        setServiceDetail(service.service);
        setExistingPDFs(service.service.pdfs)
      }
    }
  };

  useEffect(() => {
    fetchAllLocation()
  }, [])

  const fetchAllLocation = async () => {
    const data = await fetchAllLocations();

    if (data && data.locations?.length > 0) {
      const options = data.locations.map(location => ({ value: location.id, label: location.name }))
      setLocationOptions(options)
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    const isPDFValid = newPDF.length === 0 || newPDF.every((field) => field.pdfName && field.file);

    if (
      !serviceDetail.name ||
      !serviceDetail.email ||
      !serviceDetail.location_id ||
      serviceDetail.categories.length === 0 ||
      !isPDFValid ||
      !isImageValid ||
      (serviceDetail.code !== null && serviceDetail.code.length > 11) ||
      (serviceDetail.facebook && !isValidUrl(serviceDetail.facebook)) ||
      (serviceDetail.instagram && !isValidUrl(serviceDetail.instagram)) ||
      (serviceDetail.website && !isValidUrl(serviceDetail.website)) ||
      (serviceDetail.telephone && (serviceDetail.telephone.length < 8 || serviceDetail.telephone.length > 11))

    ) return;

    const selectedCategories = serviceDetail.categories.length > 0 ? serviceDetail.categories.map(category => category.value) : []

    try {
      const formData = new FormData();
      formData.append('name', serviceDetail.name);
      formData.append('description', serviceDetail.description ? serviceDetail.description.trim() : null);
      formData.append('address', serviceDetail.address ? serviceDetail.address.trim() : null);
      formData.append('email', serviceDetail.email);
      formData.append('categories', selectedCategories);
      formData.append('location_id', serviceDetail.location_id);
      formData.append('logo', serviceDetail.logo ? serviceDetail.logo : '');
      formData.append('is_active', serviceDetail.is_active ? 1 : 0);
      formData.append('code', serviceDetail.code ? serviceDetail.code.trim() : null);
      formData.append('instagram', serviceDetail.instagram ? serviceDetail.instagram.trim() : null);
      formData.append('facebook', serviceDetail.facebook ? serviceDetail.facebook.trim() : null);
      formData.append('youtube', serviceDetail.youtube ? serviceDetail.youtube.trim() : null);
      formData.append('telephone', serviceDetail.telephone ? serviceDetail.telephone.trim() : null);
      formData.append('whatsapp', serviceDetail.whatsapp ? serviceDetail.whatsapp.trim() : null);
      formData.append('website', serviceDetail.website ? serviceDetail.website.trim() : null);
      formData.append('weekly_schedule', JSON.stringify(serviceDetail.weekly_schedule));

      if (existingPDFs?.length > 0) {
        formData.append("existing_pdfs", JSON.stringify(existingPDFs));
      }

      if (newPDF?.length > 0) {
        newPDF.forEach((field, index) => {
          if (field.file && field.pdfName) {
            formData.append("pdf", field.file);
            formData.append(`pdfName_${index}`, field.pdfName);
          }
        });
      }

      const data = params.id ? await updateService(params.id, formData) : await createService(formData);
      if (!data.error) {
        navigate('/admin/services');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleRemovePDF = (id) => {
    setExistingPDFs((prevPDFs) => prevPDFs.filter(pdf => pdf.id !== id));
  }

  return (
    <Flex direction="column" minH="100vh" align="start" top="0" pt={{ sm: '125px', lg: '75px' }} position="relative" w="100%">
      <Tabs w="100%">
        <TabPanels mt="24px" maxW="100%" w="100%" mx="auto">
          <TabPanel w="100%" p="0px" mx="auto">
            <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
              Service Info
            </Text>
            <form onSubmit={handleSubmit}>
              <Flex direction="row" w="100%" justify="space-between">
                <Stack direction="column" spacing="20px" w={{ base: "100%", md: "48%" }}>
                  <CInput
                    label="Name"
                    type="text"
                    value={serviceDetail.name}
                    onChange={(value) => setServiceDetail({ ...serviceDetail, name: value })}
                    required={true}
                    validated={validated}
                    placeholder="e.g., Esthera"
                    w="100%"
                    mb="8px !important"
                  />
                  <CInput
                    label="Email"
                    type="text"
                    value={serviceDetail.email}
                    onChange={(value) => setServiceDetail({ ...serviceDetail, email: value })}
                    required={true}
                    validated={validated}
                    errorMessage="Email is required."
                    placeholder="mail@sample.com"
                    mb="8px !important"
                  />
                  <CInput
                    label="Address"
                    type="textarea"
                    value={serviceDetail.address}
                    onChange={(value) => setServiceDetail({ ...serviceDetail, address: value })}
                    validated={validated}
                    placeholder="Enter Address"
                    w="100%"
                    mb="8px !important"
                  />
                  <ServiceCategoryDropdown
                    value={serviceDetail.categories}
                    onChange={(value) => setServiceDetail({ ...serviceDetail, categories: value })}
                    required={true}
                    multiSelect={true}
                    error={serviceDetail.categories.length === 0 && validated}
                  />
                  <Dropdown
                    label="Location"
                    value={serviceDetail.location_id}
                    onChange={(e) => setServiceDetail({ ...serviceDetail, location_id: e.target.value })}
                    options={locationOptions}
                    required={true}
                    isInvalid={validated && !serviceDetail.location_id}
                    errorMessage="Please select a location."
                    mb="8px !important"
                  />
                  <CInput
                    label="Service code"
                    type="text"
                    value={serviceDetail.code}
                    onChange={(value) => setServiceDetail({ ...serviceDetail, code: value })}
                    validated={validated}
                    placeholder="NEWFFG"
                    validationRules={{ maxLength: 11 }}
                    w="100%"
                  />
                  <FormControl>
                    <FormLabel
                      display="flex"
                      ms="4px"
                      fontSize="l"
                      fontWeight="900"
                      color={textColor}
                      mb="8px"
                    >
                      Description
                    </FormLabel>
                    <Editor
                      value={serviceDetail.description}
                      onTextChange={(e) => setServiceDetail({ ...serviceDetail, description: e.htmlValue })}
                      style={{ height: '120px' }}
                    />
                  </FormControl>
                  <CInput
                    label="Active"
                    type="switch"
                    colorScheme="orange"
                    value={serviceDetail.is_active}
                    onChange={(value) =>
                      setServiceDetail({
                        ...serviceDetail,
                        is_active: value,
                      })
                    }
                    validated={validated}
                    w="100%"
                    mb="8px !important"
                  />
                  <CMultipleFileUpload
                    label="Upload PDF"
                    fileType="pdf"
                    onChange={(files) => setNewPDF(files)}
                    required={true}
                    validated={validated}
                    setValidated={setValidated}
                    multiple={true}
                    isMaxFileLimitReached={existingPDFs.length === 2 || newPDF.length === 2 || (newPDF.length + existingPDFs.length) === 2}
                  />
                  {
                    existingPDFs?.length > 0 &&
                    (
                      <ListPDF pdfs={existingPDFs} handleRemovePDF={handleRemovePDF} />
                    )
                  }
                  <ImageUpload
                    label="Logo"
                    existingImage={serviceDetail.logo && serviceDetail.logo instanceof File ? URL.createObjectURL(serviceDetail.logo) : serviceDetail.logo}
                    onImageSelect={(file) => setServiceDetail({ ...serviceDetail, logo: file })}
                    validateDimensions={true}
                    setIsImageValid={setIsImageValid}
                    width={200}
                    height={200}
                  />
                </Stack>
                <Box w={{ base: "100%", md: "48%" }}>
                  <DaysTimeTable
                    schedule={serviceDetail.weekly_schedule}
                    onScheduleChange={(updatedSchedule) => setServiceDetail((prevDetails) => ({ ...prevDetails, weekly_schedule: updatedSchedule }))}
                  />
                  <Flex direction="column" ml='15px' mt='10px'>
                    <CInput
                      label="Facebook"
                      type="text"
                      value={serviceDetail.facebook}
                      onChange={(value) => setServiceDetail({ ...serviceDetail, facebook: value })}
                      validated={validated}
                      validationRules={{ url: true }}
                      symbol={<FaFacebook />}
                      placeholder="Facebook URL"
                      w="100%"
                    />
                    <CInput
                      label="Instagram"
                      type="text"
                      value={serviceDetail.instagram}
                      onChange={(value) => setServiceDetail({ ...serviceDetail, instagram: value })}
                      validated={validated}
                      validationRules={{ url: true }}
                      symbol={<FaInstagram />}
                      placeholder="Instagram URL"
                      w="100%"
                    />
                    <CInput
                      label="Website"
                      type="text"
                      value={serviceDetail.website}
                      onChange={(value) => setServiceDetail({ ...serviceDetail, website: value })}
                      validated={validated}
                      validationRules={{ url: true }}
                      symbol={<TbWorldWww />}
                      placeholder="Website URL"
                      w="100%"
                    />
                    <CInput
                      label="Telephone"
                      type="number"
                      validationRules={{ minLength: 8, maxLength: 11 }} // Malta phone numbers are 8 digits long locally and 11 digits with the country code (+356).
                      value={serviceDetail.telephone}
                      onChange={(value) => setServiceDetail({ ...serviceDetail, telephone: value })}
                      validated={validated}
                      symbol={<BsFillTelephoneFill />}
                      placeholder="Enter telephone number."
                      w="100%"
                    />
                    <CInput
                      label="WhatsApp"
                      type="text"
                      value={serviceDetail.whatsapp}
                      onChange={(value) => setServiceDetail({ ...serviceDetail, whatsapp: value })}
                      validated={validated}
                      placeholder="Enter whatsapp detail."
                      symbol={<FaWhatsapp />}
                      w="100%"
                    />
                  </Flex>
                </Box>
              </Flex>
              <Flex justify="flex-end" align="center" mt="44px" gap="3">
                <Button
                  variant="darkBrand"
                  fontSize="sm"
                  borderRadius="10px"
                  w={{ base: '128px', md: '148px' }}
                  h="46px" type="button"
                  bg="gray"
                  color="white"
                  _hover={{ bg: 'gray.600' }}
                  onClick={() => navigate('/admin/services')}>
                  Cancel
                </Button>
                <CButton
                  w={{ base: '128px', md: '148px' }}
                  h="46px"
                  type="submit">
                  Submit
                </CButton>
              </Flex>
            </form>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
}
